import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Link from "@mui/material/Link";
import { customHistory } from "../../framework/customHistory";
import { useAppOptionValue } from "../../hooks/useAppOption";
import {
    OptionOwnerHasEasyOppTelematicsEnabled,
    optionOwnerHasTransportEnabled,
    optionOwnerHasTransportNotificationsEnabled,
} from "../../models/common/consts";
import { useAppDispatch, useAppSelector } from "../../framework/customStore";
import { fetchNotifications } from "../../store/notificationSlice";

const CHECK_INTERVAL = 3 * 60 * 1000;

export const NotificationAlerts = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const [open, setOpen] = useState(false);
    const [maintenanceCount, setMaintenanceCount] = useState(0);
    const [transportCount, setTransportCount] = useState(0);

    const maintenancePrevIdsRef = useRef<Set<string>>(new Set());
    const maintenanceInitialDone = useRef(false);
    const transportPrevCount = useRef<number | null>(null);

    const [hasTelematicsEnabled] = useAppOptionValue<boolean>(
        OptionOwnerHasEasyOppTelematicsEnabled
    );
    const [hasTransportEnabled] = useAppOptionValue<boolean>(
        optionOwnerHasTransportEnabled
    );
    const [hasTransportNotificationsEnabled] = useAppOptionValue<boolean>(
        optionOwnerHasTransportNotificationsEnabled
    );
    const hasTransportNotifications =
        hasTransportEnabled && hasTransportNotificationsEnabled;

    const desktopNotifications = useAppSelector(
        (s) => s.notifications.desktopNotifications
    );
    const transportTotalCount = useAppSelector(
        (s) => s.notifications.transportTotalCount
    );

    const fetchAll = useCallback(() => {
        void dispatch(
            fetchNotifications({
                fetchDesktop: !!hasTelematicsEnabled,
                fetchTransport: !!hasTransportNotifications,
                transportCountOnly: true,
            })
        );
    }, [dispatch, hasTelematicsEnabled, hasTransportNotifications]);

    useEffect(() => {
        fetchAll();

        const interval = setInterval(fetchAll, CHECK_INTERVAL);

        return () => clearInterval(interval);
    }, [fetchAll]);

    // Detect new desktop notifications for snackbar
    useEffect(() => {
        if (!hasTelematicsEnabled) return;

        const currentIds = new Set(
            desktopNotifications.map((n) => n.notificationId)
        );

        if (!maintenanceInitialDone.current) {
            maintenanceInitialDone.current = true;
            if (desktopNotifications.length > 0) {
                setMaintenanceCount(desktopNotifications.length);
                setOpen(true);
            }
        } else {
            const newIds = desktopNotifications.filter(
                (n) => !maintenancePrevIdsRef.current.has(n.notificationId)
            );
            if (newIds.length > 0) {
                setMaintenanceCount(desktopNotifications.length);
                setOpen(true);
            }
        }

        maintenancePrevIdsRef.current = currentIds;
    }, [desktopNotifications, hasTelematicsEnabled]);

    // Detect new transport notifications for snackbar
    useEffect(() => {
        if (!hasTransportNotifications) return;

        const total = transportTotalCount;

        if (transportPrevCount.current === null) {
            if (total > 0) {
                setTransportCount(total);
                setOpen(true);
            }
        } else if (total > transportPrevCount.current) {
            setTransportCount(total);
            setOpen(true);
        }

        transportPrevCount.current = total;
    }, [transportTotalCount, hasTransportNotifications]);

    const handleClose = useCallback(
        (_?: React.SyntheticEvent | Event, reason?: string) => {
            if (reason === "clickaway") return;
            setOpen(false);
        },
        []
    );

    const handleNavigate = useCallback(() => {
        setOpen(false);
        customHistory.push("/notifications");
    }, []);

    const totalCount = maintenanceCount + transportCount;
    const message =
        totalCount === 1
            ? t("notifications.alertSingular")
            : t("notifications.alertPlural", { count: totalCount });

    return (
        <Snackbar
            id="notification-alerts-snackbar"
            open={open}
            onClose={handleClose}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            sx={{ mt: 0 }}
        >
            <Alert
                id="notification-alerts-alert"
                onClose={handleClose}
                severity="info"
                variant="filled"
                sx={{ width: "100%" }}
            >
                <div>{message}</div>
                <div>
                    <Link
                        component="button"
                        onClick={handleNavigate}
                        sx={{
                            color: "inherit",
                            textDecorationColor: "inherit",
                        }}
                    >
                        {t("notifications.goToNotifications")}
                    </Link>
                </div>
            </Alert>
        </Snackbar>
    );
};
